import { Sparkles, type LucideIcon } from "lucide-react";
import { cn } from "@vseh/ui";

/**
 * AI Confidence System — see design extensions §41.
 *
 * AI всегда показывает: насколько уверен и откуда взял данные. Без
 * этого пользователь не доверяет автономным решениям. Отдельный визуал
 * у каждой ступени:
 *
 *   high    (≥85%) — ai-500, выглядит уверенно
 *   medium  (60-85%) — ai-400, нейтрально
 *   low     (<60%) — heritage-500, явный сигнал «проверь»
 *
 * Используется и рядом с AI-сгенерированным текстом, и на табличных
 * полях, заполненных AI ("год выпуска: 2018"), и в результатах
 * matching'а ("эти заявки похожи: 78%").
 */
export interface ConfidenceBadgeProps {
  /** В процентах [0..100]. */
  value: number;
  /** Откуда AI взял суждение. Кратко. */
  source?: string;
  icon?: LucideIcon;
  size?: "sm" | "md";
  className?: string;
}

function levelFor(value: number): "high" | "medium" | "low" {
  if (value >= 85) return "high";
  if (value >= 60) return "medium";
  return "low";
}

const palette = {
  high:   { bg: "bg-ai-50",       text: "text-ai-800",       dot: "bg-ai-500" },
  medium: { bg: "bg-ai-50",       text: "text-ai-700",       dot: "bg-ai-400" },
  low:    { bg: "bg-heritage-50", text: "text-heritage-800", dot: "bg-heritage-500" },
} as const;

export function ConfidenceBadge({
  value,
  source,
  icon: Icon = Sparkles,
  size = "sm",
  className,
}: ConfidenceBadgeProps): JSX.Element {
  const level = levelFor(value);
  const colors = palette[level];
  const isSm = size === "sm";

  return (
    <span
      role="status"
      aria-label={`Уверенность AI: ${value}%${source ? `, источник: ${source}` : ""}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm font-medium",
        isSm ? "px-1.5 py-0.5 text-2xs" : "px-2 py-1 text-xs",
        colors.bg,
        colors.text,
        className,
      )}
    >
      <Icon aria-hidden="true" className={isSm ? "size-3" : "size-3.5"} />
      <span className="tabular-nums">{Math.round(value)}%</span>
      {source && (
        <>
          <span aria-hidden="true" className="opacity-50">·</span>
          <span className="font-normal opacity-80">{source}</span>
        </>
      )}
    </span>
  );
}
